// src/components/ServiceProductManagement.js

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import 'animate.css';
import { formatCurrency } from '../utils/currency';
import PaginationBar from './shared/PaginationBar';
import { usePagination } from '../hooks/usePagination';

// Helper component for permission denied message
const PermissionDenied = () => (
    <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
            <svg className="mx-auto h-16 w-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="mt-4 text-2xl font-bold text-gray-800">Access Denied</h2>
            <p className="mt-2 text-gray-600">You do not have the necessary permissions to view this page.</p>
        </div>
    </div>
);


function ServiceProductManagement({
  serviceProducts,
  authAxios,
  fetchServiceProducts,
  showFormMessage,
  userRole,
}) {
  // --- RBAC Permissions ---
  // Service catalog management is opened up beyond PARTNER/SUPERUSER so that
  // accountants and matter attorneys can curate the items they need without
  // a partner bottleneck (server-side rules still enforce firm scoping).
  const permissions = useMemo(() => ({
      canViewPage: ['PARTNER', 'ASSOCIATE', 'PARALEGAL', 'ACCOUNTANT', 'SUPERUSER'].includes(userRole),
      canManage: ['PARTNER', 'ASSOCIATE', 'ACCOUNTANT', 'SUPERUSER'].includes(userRole),
      // Create-only: paralegals may add a new catalog entry but not edit or
      // delete/archive an existing one (that stays canManage-gated below).
      canCreate: ['PARTNER', 'ASSOCIATE', 'ACCOUNTANT', 'PARALEGAL', 'SUPERUSER'].includes(userRole),
  }), [userRole]);

  // --- View Mode State ---
  const [viewMode, setViewMode] = useState('tiles'); // 'list' or 'tiles'

  // Service Product specific states
  const [showAddServiceProductForm, setShowAddServiceProductForm] = useState(false);
  const [showEditServiceProductModal, setShowEditServiceProductModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedProductDetails, setSelectedProductDetails] = useState(null);

  // New service product form data
  const [newServiceProduct, setNewServiceProduct] = useState({
    name: '', description: '', price: '', category: 'OTHER', is_active: true, type: 'SERVICE'
  });

  // States for editing service products
  const [editingServiceProduct, setEditingServiceProduct] = useState(null);

  // --- Archive Confirmation State ---
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [itemToArchive, setItemToArchive] = useState(null);


  // --- Search, Filter, and Sort States ---
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active', 'inactive', or 'all'
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });

  // Restore saved view preferences from localStorage
  useEffect(() => {
    try {
      const p = JSON.parse(localStorage.getItem('services.ui') || '{}');
      if (p.viewMode) setViewMode(p.viewMode);
      if (typeof p.categoryFilter === 'string') setCategoryFilter(p.categoryFilter);
      if (typeof p.statusFilter === 'string') setStatusFilter(p.statusFilter);
      if (p.sortConfig && p.sortConfig.key) setSortConfig(p.sortConfig);
    } catch (_) {}
  }, []);
  useEffect(() => {
    try { localStorage.setItem('services.ui', JSON.stringify({ viewMode, categoryFilter, statusFilter, sortConfig })); } catch (_) {}
  }, [viewMode, categoryFilter, statusFilter, sortConfig]);

  const serviceProductCategoryOptions = [
      { value: 'Consultation', label: 'Consultation' },
      { value: 'Document Preparation', label: 'Document Preparation' },
      { value: 'Court Filing', label: 'Court Filing' },
      { value: 'Advisory', label: 'Advisory' },
      { value: 'Retainer', label: 'Retainer' },
      { value: 'OTHER', label: 'Other' },
  ];

  // --- Filtered and Sorted Data ---
  const filteredAndSortedProducts = useMemo(() => {
    let sortedProducts = [...serviceProducts];

    // Filtering logic
    sortedProducts = sortedProducts.filter(sp => {
      const matchesSearch = sp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (sp.description && sp.description.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesCategory = categoryFilter === 'all' || sp.category === categoryFilter;
      const matchesStatus = statusFilter === 'all' || (statusFilter === 'active' ? sp.is_active : !sp.is_active);

      return matchesSearch && matchesCategory && matchesStatus;
    });

    // Sorting logic
    if (sortConfig.key) {
      sortedProducts.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    return sortedProducts;
  }, [serviceProducts, searchTerm, categoryFilter, statusFilter, sortConfig]);

  const { pageItems: pagedProducts, paginationProps } = usePagination(filteredAndSortedProducts);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };


  // --- Service Product Form Input Change Handlers ---
  const handleServiceProductInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    setNewServiceProduct({ ...newServiceProduct, [name]: val });
  };

  // --- Service Product Form Submission Handler ---
  const handleServiceProductSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!permissions.canCreate) {
        showFormMessage('danger', 'You do not have permission to add service products.');
        return;
    }
    showFormMessage('', '');

    try {
      const payload = { ...newServiceProduct };
      if (payload.description == null) payload.description = '';
      await authAxios.post('service-products/', payload);
      showFormMessage('success', 'Service Product added successfully!');
      setShowAddServiceProductForm(false);
      setNewServiceProduct({
        name: '', description: '', price: '', category: 'OTHER', is_active: true, type: 'SERVICE'
      });
      fetchServiceProducts();
    } catch (err) {
      console.error('Error adding Service Product:', err.response ? err.response.data : err.message);
      const data = err.response?.data;
      let errorMessage = 'Failed to add Service Product.';
      if (data) {
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (typeof data === 'object') {
          const fieldMessages = Object.entries(data)
            .map(([field, msgs]) => {
              const text = Array.isArray(msgs) ? msgs.join(' ') : String(msgs);
              return `${field}: ${text}`;
            })
            .join(' | ');
          if (fieldMessages) errorMessage = fieldMessages;
        }
      }
      showFormMessage('danger', errorMessage.trim());
    }
  }, [authAxios, newServiceProduct, fetchServiceProducts, showFormMessage, permissions.canCreate]);

  // --- Edit Service Product Handlers ---
  const handleEditingServiceProductChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    setEditingServiceProduct(prev => ({ ...prev, [name]: val }));
  };

  const handleEditServiceProductSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!editingServiceProduct || !permissions.canManage) {
        showFormMessage('danger', 'You do not have permission to edit service products.');
        return;
    };
    showFormMessage('', '');
    try {
      const payload = { ...editingServiceProduct };
      if (payload.description == null) payload.description = '';
      await authAxios.patch(`service-products/${editingServiceProduct.id}/`, payload);
      showFormMessage('success', 'Service Product updated successfully!');
      setShowEditServiceProductModal(false);
      setEditingServiceProduct(null);
      fetchServiceProducts();
    } catch (err) {
      console.error('Error updating Service Product:', err.response ? err.response.data : err.message);
      const data = err.response?.data;
      let errorMessage = 'Failed to update Service Product.';
      if (data) {
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (typeof data === 'object') {
          const fieldMessages = Object.entries(data)
            .map(([field, msgs]) => {
              const text = Array.isArray(msgs) ? msgs.join(' ') : String(msgs);
              return `${field}: ${text}`;
            })
            .join(' | ');
          if (fieldMessages) errorMessage = fieldMessages;
        }
      }
      showFormMessage('danger', errorMessage.trim());
    }
  }, [editingServiceProduct, authAxios, fetchServiceProducts, showFormMessage, permissions.canManage]);

  // --- Archive/Activate Handler ---
  const handleArchiveToggle = useCallback((serviceProduct) => {
      if (!permissions.canManage) {
          showFormMessage('warning', 'You do not have permission to change the status of service products.');
          return;
      }
      setItemToArchive(serviceProduct);
      setShowArchiveConfirm(true);
  }, [permissions.canManage, showFormMessage]);

  const confirmArchiveToggle = useCallback(async () => {
    if (!itemToArchive) return;
    const action = itemToArchive.is_active ? 'archive' : 'activate';
    try {
        await authAxios.patch(`service-products/${itemToArchive.id}/`, { is_active: !itemToArchive.is_active });
        showFormMessage('success', `Service/Product ${action}d successfully!`);
        fetchServiceProducts();
    } catch (error) {
        console.error(`Error ${action}ing service/product:`, error.response ? error.response.data : error.message);
        showFormMessage('danger', `Failed to ${action} service/product.`);
    } finally {
        setShowArchiveConfirm(false);
        setItemToArchive(null);
    }
  }, [authAxios, itemToArchive, showFormMessage, fetchServiceProducts]);


  // --- Modal Handlers ---
  const openEditServiceProductModal = useCallback((serviceProduct) => {
    if (!permissions.canManage) {
        showFormMessage('warning', 'You do not have permission to edit service products.');
        return;
    }
    setEditingServiceProduct(serviceProduct);
    setShowEditServiceProductModal(true);
    setShowDetailsModal(false);
  }, [permissions.canManage, showFormMessage]);

  const closeEditServiceProductModal = useCallback(() => {
    setEditingServiceProduct(null);
    setShowEditServiceProductModal(false);
  }, []);

  const openDetailsModal = useCallback((serviceProduct) => {
    setSelectedProductDetails(serviceProduct);
    setShowDetailsModal(true);
  }, []);

  const closeDetailsModal = useCallback(() => {
    setSelectedProductDetails(null);
    setShowDetailsModal(false);
  }, []);

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '▲▼';
    if (sortConfig.direction === 'ascending') return '▲';
    return '▼';
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (error) {
      console.error("Error parsing date for display:", dateString, error);
      return dateString;
    }
  };
  
  if (!permissions.canViewPage) {
    return <PermissionDenied />;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Services & Products</h2>
      
      <div className="flex justify-between items-center mb-6">
        {permissions.canCreate && (
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center"
              onClick={() => setShowAddServiceProductForm(!showAddServiceProductForm)}>
              <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={showAddServiceProductForm ? "M6 18L18 6M6 6l12 12" : "M12 6v12m6-6H6"} />
              </svg>
              {showAddServiceProductForm ? 'Cancel' : 'New Service/Product'}
            </button>
        )}
        <div className="flex space-x-2" style={{ marginLeft: permissions.canCreate ? '0' : 'auto' }}>
          <button className={`px-4 py-2 rounded-md text-sm font-medium ${viewMode === 'list' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => setViewMode('list')}>
            List
          </button>
          <button className={`px-4 py-2 rounded-md text-sm font-medium ${viewMode === 'tiles' ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700'}`} onClick={() => setViewMode('tiles')}>
            Tiles
          </button>
        </div>
      </div>

       {/* Search and Filter Controls */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <input type="text" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by name or description..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
             <select className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                <option value="all">All Categories</option>
                {serviceProductCategoryOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
            <select className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="all">All Statuses</option>
            </select>
             <button className="w-full bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" onClick={() => { setSearchTerm(''); setCategoryFilter('all'); setStatusFilter('active'); }}>
                Clear Filters
            </button>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <div className="px-6 py-4 bg-gray-100 border-b border-gray-200">
              <h5 className="text-lg font-bold text-gray-800">All Services & Products</h5>
          </div>
          <div className="p-6">
              {viewMode === 'list' ? (
                  <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                              <tr>
                                  <th onClick={() => requestSort('name')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Name {getSortIcon('name')}</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                                  <th onClick={() => requestSort('category')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Category {getSortIcon('category')}</th>
                                  <th onClick={() => requestSort('price')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">Price {getSortIcon('price')}</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                              </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                              {pagedProducts.map(sp => (
                                  <tr key={sp.id} className="hover:bg-gray-50">
                                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{sp.name}</td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{sp.description}</td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{sp.category_display}</td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(sp.price)}</td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${sp.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                            {sp.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-4 flex items-center">
                                          {permissions.canManage && (
                                            <button className="text-blue-600 hover:text-blue-900 flex items-center" onClick={() => openEditServiceProductModal(sp)}>
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>
                                                Edit
                                            </button>
                                          )}
                                          <button className="text-gray-600 hover:text-gray-900 flex items-center" onClick={() => openDetailsModal(sp)}>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C3.732 4.943 7.523 3 10 3s6.268 1.943 9.542 7c-3.274 5.057-7.03 7-9.542 7S3.732 15.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
                                            View
                                          </button>
                                          {permissions.canManage && (
                                            <button
                                                className={`flex items-center text-sm font-medium ${sp.is_active ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}`}
                                                onClick={() => handleArchiveToggle(sp)}
                                            >
                                                {sp.is_active ? (
                                                    <><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" /><path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" /></svg>Archive</>
                                                ) : (
                                                    <><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>Activate</>
                                                )}
                                            </button>
                                          )}
                                      </td>
                                  </tr>
                              ))}
                          </tbody>
                      </table>
                  </div>
              ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {pagedProducts.map(sp => (
                          <div key={sp.id} className="bg-white shadow-md rounded-lg overflow-hidden flex flex-col">
                              <div className="p-6 flex-grow">
                                  <h5 className="text-lg font-bold text-gray-800">{sp.name}</h5>
                                  <p className="text-sm text-gray-600 mt-2">{sp.category_display}</p>
                                  <p className="text-sm text-gray-600 mt-2 flex-grow">{sp.description || 'No description provided.'}</p>
                                  <p className="text-2xl font-bold text-green-600 mt-4">{formatCurrency(sp.price)}</p>
                              </div>
                              <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-4">
                                  {permissions.canManage && (
                                    <button className="text-blue-600 hover:text-blue-900 flex items-center text-sm" onClick={() => openEditServiceProductModal(sp)}>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>
                                        Edit
                                    </button>
                                  )}
                                  <button className="text-gray-600 hover:text-gray-900 flex items-center text-sm" onClick={() => openDetailsModal(sp)}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z" /><path fillRule="evenodd" d="M.458 10C3.732 4.943 7.523 3 10 3s6.268 1.943 9.542 7c-3.274 5.057-7.03 7-9.542 7S3.732 15.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" /></svg>
                                    View
                                  </button>
                                  {permissions.canManage && (
                                    <button
                                        className={`flex items-center text-sm font-medium ${sp.is_active ? 'text-yellow-600 hover:text-yellow-900' : 'text-green-600 hover:text-green-900'}`}
                                        onClick={() => handleArchiveToggle(sp)}
                                    >
                                        {sp.is_active ? (
                                            <><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z" /><path fillRule="evenodd" d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" /></svg>Archive</>
                                        ) : (
                                            <><svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>Activate</>
                                        )}
                                    </button>
                                  )}
                              </div>
                          </div>
                      ))}
                  </div>
              )}
          </div>
          <PaginationBar {...paginationProps} emptyLabel="No services or products found." />
      </div>

    {/* Add Service/Product Side Panel */}
    {showAddServiceProductForm && permissions.canCreate && (
        <div className="fixed inset-0 overflow-hidden z-50">
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowAddServiceProductForm(false)}></div>
                <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
                    <div className={`w-screen max-w-md transform transition ease-in-out duration-500 ${showAddServiceProductForm ? 'translate-x-0' : 'translate-x-full'}`}>
                        <form onSubmit={handleServiceProductSubmit} className="h-full flex flex-col bg-white shadow-xl">
                            <div className="flex-shrink-0 px-4 py-4 flex items-center justify-between border-b">
                                <h2 className="text-lg font-medium text-gray-900">New Service/Product</h2>
                                <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500" onClick={() => setShowAddServiceProductForm(false)}>
                                    <span className="sr-only">Close panel</span>
                                    <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto">
                                <div className="p-6 space-y-6">
                                    <div className="grid grid-cols-1 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Name: *</label>
                                            <input type="text" name="name" value={newServiceProduct.name} onChange={handleServiceProductInputChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Category: *</label>
                                            <select name="category" value={newServiceProduct.category} onChange={handleServiceProductInputChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required>
                                                {serviceProductCategoryOptions.map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Price: *</label>
                                            <input type="number" step="0.01" name="price" value={newServiceProduct.price} onChange={handleServiceProductInputChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Description:</label>
                                            <textarea name="description" value={newServiceProduct.description} onChange={handleServiceProductInputChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" rows="3"></textarea>
                                        </div>
                                        <div className="flex items-center">
                                            <input type="checkbox" name="is_active" id="isActiveCheck" checked={newServiceProduct.is_active} onChange={handleServiceProductInputChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                            <label htmlFor="isActiveCheck" className="ml-2 block text-sm text-gray-900">Active</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex-shrink-0 px-4 py-4 flex justify-end space-x-4 border-t">
                                <button type="button" onClick={() => setShowAddServiceProductForm(false)} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">Cancel</button>
                                <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">Add Service/Product</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )}

    {/* Details Modal (Side Panel from Right) */}
    {showDetailsModal && selectedProductDetails && (
        <div className="fixed inset-0 overflow-hidden z-50">
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeDetailsModal}></div>
                <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
                    <div className={`relative w-screen max-w-md transform transition ease-in-out duration-500 ${showDetailsModal ? 'translate-x-0' : 'translate-x-full'}`}>
                        <div className="h-full flex flex-col bg-white shadow-xl">
                            <div className="flex-shrink-0 px-4 py-4 flex items-center justify-between border-b">
                                <h2 className="text-lg font-medium text-gray-900">Details: {selectedProductDetails.name}</h2>
                                <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500" onClick={closeDetailsModal}>
                                    <span className="sr-only">Close panel</span>
                                    <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto">
                                <div className="p-6 space-y-4">
                                    <ul className="divide-y divide-gray-200">
                                        <li className="py-2"><strong>Name:</strong> {selectedProductDetails.name}</li>
                                        <li className="py-2"><strong>Category:</strong> {selectedProductDetails.category_display}</li>
                                        <li className="py-2"><strong>Price:</strong> {formatCurrency(selectedProductDetails.price)}</li>
                                        <li className="py-2"><strong>Status:</strong> <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${selectedProductDetails.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{selectedProductDetails.is_active ? 'Active' : 'Inactive'}</span></li>
                                        <li className="py-2"><strong>Description:</strong> {selectedProductDetails.description || 'N/A'}</li>
                                        <li className="py-2"><strong>Date Created:</strong> {formatDateForDisplay(selectedProductDetails.created_at)}</li>
                                        <li className="py-2"><strong>Last Updated:</strong> {formatDateForDisplay(selectedProductDetails.updated_at)}</li>
                                    </ul>
                                </div>
                            </div>
                            <div className="flex-shrink-0 px-4 py-4 flex justify-end space-x-2 border-t">
                                <button type="button" className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" onClick={closeDetailsModal}>Close</button>
                                {permissions.canManage && (
                                    <button type="button" className="bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" onClick={() => openEditServiceProductModal(selectedProductDetails)}>Edit</button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )}

    {/* Archive/Activate Confirmation Modal */}
    {showArchiveConfirm && itemToArchive && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-auto">
                <div className="sm:flex sm:items-start">
                    <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${itemToArchive.is_active ? 'bg-yellow-100' : 'bg-green-100'} sm:mx-0 sm:h-10 sm:w-10`}>
                        {itemToArchive.is_active ? (
                            <svg className="h-6 w-6 text-yellow-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        ) : (
                            <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                        )}
                    </div>
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                            Confirm {itemToArchive.is_active ? 'Archival' : 'Activation'}
                        </h3>
                        <div className="mt-2">
                            <p className="text-sm text-gray-500">
                                Are you sure you want to {itemToArchive.is_active ? 'archive' : 'activate'} "{itemToArchive.name}"?
                                <br />
                                {itemToArchive.is_active ? 'It will no longer be available for new assignments.' : 'It will become available for use again.'}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                        type="button"
                        className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${itemToArchive.is_active ? 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500' : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'}`}
                        onClick={confirmArchiveToggle}
                    >
                        {itemToArchive.is_active ? 'Archive' : 'Activate'}
                    </button>
                    <button
                        type="button"
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                        onClick={() => setShowArchiveConfirm(false)}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    )}

    {/* Edit Service Product Side Panel */}
    {showEditServiceProductModal && editingServiceProduct && permissions.canManage && (
        <div className="fixed inset-0 overflow-hidden z-50">
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={closeEditServiceProductModal}></div>
                <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
                    <div className={`w-screen max-w-md transform transition ease-in-out duration-500 ${showEditServiceProductModal ? 'translate-x-0' : 'translate-x-full'}`}>
                        <form onSubmit={handleEditServiceProductSubmit} className="h-full flex flex-col bg-white shadow-xl">
                            <div className="flex-shrink-0 px-4 py-4 flex items-center justify-between border-b">
                                <h2 className="text-lg font-medium text-gray-900">Edit Service Product</h2>
                                <button type="button" className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500" onClick={closeEditServiceProductModal}>
                                    <span className="sr-only">Close panel</span>
                                    <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto">
                                <div className="p-6 space-y-6">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700">Name:</label>
                                    <input type="text" name="name" value={editingServiceProduct.name || ''} onChange={handleEditingServiceProductChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                  </div>
                                   <div>
                                      <label className="block text-sm font-medium text-gray-700">Category:</label>
                                      <select name="category" value={editingServiceProduct.category} onChange={handleEditingServiceProductChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                                          {serviceProductCategoryOptions.map(option => (<option key={option.value} value={option.value}>{option.label}</option>))}
                                      </select>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700">Price:</label>
                                    <input type="number" step="0.01" name="price" value={editingServiceProduct.price || ''} onChange={handleEditingServiceProductChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" required />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700">Description:</label>
                                    <textarea name="description" value={editingServiceProduct.description || ''} onChange={handleEditingServiceProductChange} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" rows="3"></textarea>
                                  </div>
                                  <div className="flex items-center">
                                    <input type="checkbox" name="is_active" id="editIsActiveCheck" checked={editingServiceProduct.is_active} onChange={handleEditingServiceProductChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                                    <label htmlFor="editIsActiveCheck" className="ml-2 block text-sm text-gray-900">Active</label>
                                  </div>
                                </div>
                            </div>
                            <div className="flex-shrink-0 px-4 py-4 flex justify-end space-x-4 border-t">
                                <button type="button" className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" onClick={closeEditServiceProductModal}>Cancel</button>
                                <button type="submit" className="bg-green-600 text-white px-4 py-2 rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500">Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )}

    </div>
  );
}

export default ServiceProductManagement;

